import { HubConnection } from "@microsoft/signalr";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import {
  SignalRApiResponse,
  useShapeFileInvalidateQuery,
} from "./useProcessingFileUpdate";
import { UseSignalRReturn } from "./useSignalR";

type UseSignalrRApiResponseHandlerProps = {
  listenerKey: string;
  successFunction: (projectId: number) => void;
  finalFunction?: (projectId: number) => void;
};

export const useSignalrRApiResponseHandler = ({
  connection,
  connected,
  listenerKey,
  finalFunction,
  successFunction,
}: Omit<UseSignalRReturn, "initialLoading" | "connection"> & {
  connection: HubConnection | null;
} & UseSignalrRApiResponseHandlerProps) => {
  const { invalidateQueries } = useShapeFileInvalidateQuery();
  const qc = useQueryClient();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const handleApiError = useApiErrorHandler();
  const { t } = useTranslation();
  React.useEffect(() => {
    if (connected && connection) {
      // Add listeners

      connection.on(
        listenerKey,
        (projectId: number, res: SignalRApiResponse) => {
          if (
            res?.statusCode.toString()?.[0] === "2" &&
            "resultKey" in res.value
          ) {
            successFunction(projectId);

            const key = enqueueSnackbar(
              t(`apiResponses:${res.value.resultKey}`),
              {
                key: res.value.resultKey,
                variant: "success",
                preventDuplicate: true,
                onClick: () => closeSnackbar(key),
              },
            );
          } else handleApiError(res.value as unknown as Error);
          finalFunction?.(projectId);
        },
      );
    }
    return () => {
      // Remove listeners

      connection?.off(listenerKey);
    };
  }, [
    closeSnackbar,
    connected,
    connection,
    enqueueSnackbar,
    finalFunction,
    handleApiError,
    invalidateQueries,
    listenerKey,
    qc,
    successFunction,
    t,
  ]);
};
